<script setup lang="ts">
import { setConfig } from '@barbanet/api-service'
import { useAppConfig } from '@barbanet/app-config'
import { PermissionRouteGuard, usePermission } from '@barbanet/permissions'

import { BHeader } from '@barbanet/ui-kit/components'
import { useExceptionHandler } from '@barbanet/ui-kit/composables'

import sidebarItems from './constants/sidebar-items'
import { userMeService } from './domains/user/services/user'
import { useAccountStore } from './stores/account-store'

const { apiServer, appVersion, apiServerDefaultVersion } = useAppConfig()
const accountStore = useAccountStore()
const { t } = useI18n()
const router = useRouter()
const { sync: syncPermissions } = usePermission()

const userInfo = computed(() => {
	if (accountStore.isLoggedIn && accountStore.userInfo) {
		return {
			displayName: accountStore.userInfo.displayName
			// TODO: other fields should provide by the backend and not implemented yet
		}
	}

	return null
})

setConfig({
	apiServer,
	appVersion: appVersion || undefined,
	apiServerDefaultVersion,
	credentials:
		accountStore.isLoggedIn && accountStore.credentials
			? {
					accessToken: accountStore.credentials.accessToken,
					refreshToken: accountStore.credentials.refreshToken
				}
			: null,
	onRefreshToken(newCredentials) {
		accountStore.setCredentials(newCredentials)
	},
	onRevokeToken() {
		accountStore.logout()
	}
})

const menuItems = ref<InstanceType<typeof BHeader>['$props']['menuItems']>([])
const onMenuItemClick = (itemId: string) => {
	if (itemId === 'logout') {
		accountStore.logout()
	}
}

onMounted(async () => {
	await router.isReady()
	menuItems.value = [
		{
			id: 'logout',
			text: t('shared.logout'),
			color: 'error',
			icon: 'logout-02'
		}
	]

	if (accountStore.isLoggedIn) {
		try {
			const response = await userMeService()
			await syncPermissions()

			if (response && response.data) {
				accountStore.setUserInfo(response.data.content)
			}
		} catch (error) {
			useExceptionHandler(error)
		}
	}
})
</script>

<template>
	<PermissionRouteGuard>
		<BLayout
			:sidebar-props="{
				items: sidebarItems,
				dashboardTo: { name: 'DashboardPage' }
			}"
			:header-props="{
				userInfo,
				menuItems
			}"
			@click:menu-item="onMenuItemClick"
		>
			<RouterView />
		</BLayout>
	</PermissionRouteGuard>
</template>
